/**
 * Grid / panel visualizations: swot, pestel, quadrant, table, pros-and-cons,
 * versus, problem-solution, transformation.
 * Geometry per design-notes/viz-import/LAYOUT_RECIPES.md.
 */

import { registerViz } from "../registry.js";
import { itemsOf, optStr, type VizItem, type VizSpec } from "../types.js";
import type { VizContext } from "../context.js";
import type { RoleStyle } from "../../style/presets.js";
import { lerp } from "./util.js";

/** Accent color that reads on top of a role-styled panel across fill modes:
 *  the raw palette color on unfilled (outline) panels, contrast ink on filled ones. */
const onPanel = (role: RoleStyle): string => (role.fill ? role.textColor : role.color);

/** Body-text color inside a role-styled panel. */
const inPanel = (ctx: VizContext, role: RoleStyle): string => (role.fill ? role.textColor : ctx.ink);

/** List option (e.g. `xLabels: ["Not urgent", "Urgent"]`). */
function optList(opts: Record<string, unknown>, key: string): string[] | undefined {
  const v = opts[key];
  return Array.isArray(v) ? v.map(String) : undefined;
}

interface BulletOpts {
  dotD?: number;
  dotColor: string;
  textColor: string;
  maxW: number;
  size?: number;
  pitch?: number;
  /** Tag each entry's dot+text via ctx.item(entry.id) — for lists whose entries
   *  are the template's own data items (not children of an enclosing item). */
  scopeItems?: boolean;
}

/** Bullet list (dot + wrapped text); returns the height consumed. */
function bulletList(ctx: VizContext, entries: VizItem[], x: number, y: number, opts: BulletOpts): number {
  const dotD = opts.dotD ?? 12;
  const size = opts.size ?? 15;
  const pitch = opts.pitch ?? 42;
  entries.forEach((entry, i) => {
    const draw = (): void => {
      const cy = y + i * pitch + pitch / 2;
      ctx.shape("circle", x, cy - dotD / 2, dotD, dotD, {
        stroke: opts.dotColor,
        fill: opts.dotColor,
        fillStyle: "solid",
        strokeWidth: 1,
        roughness: ctx.preset.roughness,
      });
      ctx.label(ctx.wrap(entry.label, opts.maxW, size, undefined, 2), x + dotD + 12, cy, { size, color: opts.textColor, align: "left" });
    };
    if (opts.scopeItems) ctx.item(entry.id, draw);
    else draw();
  });
  return entries.length * pitch;
}

/** Run `fn` scoped to `item` when it exists; fallback-only parts stay untagged. */
function inItem(ctx: VizContext, item: VizItem | undefined, fn: () => void): void {
  if (item) ctx.item(item.id, fn);
  else fn();
}

const lineCount = (wrapped: string): number => wrapped.split("\n").length;

// ---- swot -------------------------------------------------------------------

registerViz({
  name: "swot",
  category: "Business Frameworks",
  summary: "S/W/O/T panels (2×2 by default) with a big display letter, title and bullets.",
  entryKinds: ["item", "section"],
  options: [{ name: "layout", type: "grid|stack", description: "grid (default) tiles the panels 2×2; stack keeps one column" }],
  sweetSpot: { min: 4, max: 4 },
  generate(spec: VizSpec, ctx: VizContext) {
    const items = itemsOf(spec, "item", "section");
    const n = Math.max(items.length, 1);
    // A single column of four panels is ~1:1.6 tall-and-narrow, which the
    // gallery fit then shrinks below reading size; 2×2 lands near the frame's
    // own 2.4:1 and buys ~2× the type. `layout: stack` keeps the old column.
    const stacked = (optStr(spec.options, "layout") ?? "grid") === "stack";
    const cols = stacked ? 1 : 2;
    const W = stacked ? 560 : 440;
    const gap = 18;
    const pad = 22;
    const contentX = stacked ? 190 : 150;
    const contentW = W - contentX - pad;
    const pitch = stacked ? 46 : 40;
    const bulletSize = stacked ? 15 : 17;
    const letterSize = stacked ? 110 : 88;
    const letterX = stacked ? 95 : 75;

    // Uniform panel height fitting the busiest panel.
    let panelH = stacked ? 160 : 150;
    for (const item of items) {
      let need = 76; // padding + title row
      if (item.detail) need += lineCount(ctx.wrap(item.detail, contentW, 15)) * 19 + 10;
      need += item.children.length * pitch + 18;
      panelH = Math.max(panelH, need);
    }

    items.forEach((item, i) =>
      ctx.item(item.id, () => {
        const role = ctx.role(i, { n, color: item.color });
        const x = (i % cols) * (W + gap);
        const y = Math.floor(i / cols) * (panelH + gap);
        ctx.shape("rectangle", x, y, W, panelH, role, { id: ctx.uid(item.id), style: { roundness: role.roundness ?? 8 } });
        const accent = onPanel(role);
        const body = inPanel(ctx, role);
        // big display letter in the left third
        const letter = (item.label.trim()[0] ?? "?").toUpperCase();
        ctx.label(letter, x + letterX, y + panelH / 2, { size: letterSize, color: accent, weight: 700, font: "heading" });
        if (item.icon) ctx.icon(item.icon, x + letterX, y + panelH - 30, 28, accent);
        let cy = y + pad + 12;
        ctx.label(ctx.wrap(item.label, contentW, 20, "heading", 2), x + contentX, cy, {
          size: 20,
          color: accent,
          align: "left",
          vAnchor: "top",
          font: "heading",
          weight: ctx.preset.fonts.headingWeight,
        });
        cy += 36;
        if (item.detail) {
          const text = ctx.wrap(item.detail, contentW, 15);
          ctx.label(text, x + contentX, cy, { size: 15, color: body, align: "left", vAnchor: "top" });
          cy += lineCount(text) * 19 + 10;
        }
        bulletList(ctx, item.children, x + contentX, cy, { dotColor: accent, textColor: body, maxW: contentW - 22, pitch, size: bulletSize });
      }),
    );
  },
});

// ---- pestel -----------------------------------------------------------------

registerViz({
  name: "pestel",
  category: "Business Frameworks",
  summary: "Vertical category cards — big letter, title, summary, bullets.",
  entryKinds: ["item", "card", "category", "factor"],
  sweetSpot: { min: 4, max: 6 },
  generate(spec: VizSpec, ctx: VizContext) {
    const items = itemsOf(spec, "item", "card", "category", "factor");
    const n = Math.max(items.length, 1);
    const W = 182;
    const gap = 14;
    const pad = 15;
    const contentW = W - pad * 2;

    // Uniform card height fitting the busiest card. The floor only guards a
    // near-empty card: a fixed 340 left ~40% of every demo card blank, and the
    // fit here is width-bound, so the extra height bought no type size.
    let cardH = 200;
    for (const item of items) {
      let need = 96 + lineCount(ctx.wrap(item.label, contentW, 20, "heading", 3)) * 25 + 14;
      if (item.detail) need += lineCount(ctx.wrap(item.detail, contentW, 15)) * 19 + 14;
      need += item.children.length * 54 + pad;
      cardH = Math.max(cardH, need);
    }

    items.forEach((item, i) =>
      ctx.item(item.id, () => {
        const role = ctx.role(i, { n, color: item.color });
        const x = i * (W + gap);
        ctx.shape("rectangle", x, 0, W, cardH, role, { id: ctx.uid(item.id), style: { roundness: role.roundness ?? 8 } });
        const accent = onPanel(role);
        const body = inPanel(ctx, role);
        const cx = x + W / 2;
        const letter = (item.label.trim()[0] ?? "?").toUpperCase();
        ctx.label(letter, cx, 52, { size: 64, color: accent, weight: 700, font: "heading" });
        if (item.icon) ctx.icon(item.icon, cx, 52, 30, accent);
        let cy = 96;
        const title = ctx.wrap(item.label, contentW, 20, "heading", 3);
        ctx.label(title, cx, cy, { size: 20, color: accent, vAnchor: "top", font: "heading", weight: ctx.preset.fonts.headingWeight });
        cy += lineCount(title) * 25 + 14;
        if (item.detail) {
          const text = ctx.wrap(item.detail, contentW, 15);
          ctx.label(text, x + pad, cy, { size: 15, color: body, align: "left", vAnchor: "top" });
          cy += lineCount(text) * 19 + 14;
        }
        bulletList(ctx, item.children, x + pad, cy, { dotD: 7, dotColor: accent, textColor: body, maxW: contentW - 22, pitch: 54 });
      }),
    );
  },
});

// ---- quadrant ---------------------------------------------------------------

registerViz({
  name: "quadrant",
  aliases: ["2x2", "matrix"],
  category: "Comparison",
  summary: "2×2 matrix on double-headed axes; items in TL/TR/BL/BR order.",
  entryKinds: ["item", "quadrant"],
  options: [
    { name: "xLabels", type: "string[]", description: "x-axis end captions as [negative, positive]" },
    { name: "yLabels", type: "string[]", description: "y-axis end captions as [negative, positive]" },
  ],
  sweetSpot: { min: 4, max: 4 },
  generate(spec: VizSpec, ctx: VizContext) {
    const items = itemsOf(spec, "item", "quadrant").slice(0, 4);
    // Plot area sized to the frame's own ~2.2:1 band (a squarer matrix fits
    // height-bound and shrinks every glyph); the caption margins are part of
    // the design box, so the fit stays width-bound.
    const marginX = 104;
    const marginY = 30;
    const plotW = 820;
    const g = 12; // gutter between a panel and the axis it touches
    const qw = plotW / 2 - g;
    const maxW = qw - 48;
    // each region's content block, measured up front so the panel height fits
    // the busiest region (a fixed height overflowed with three bullets)
    const blocks = items.map((item) => {
      const bullets = item.children.slice(0, 3);
      const detailText = !bullets.length && item.detail ? ctx.wrap(item.detail, maxW, 18) : undefined;
      const nameText = ctx.wrap(item.label, maxW, 24, "heading", 2);
      const nameH = lineCount(nameText) * 30;
      const blockH = (item.icon ? 58 : 0) + nameH + (bullets.length ? 4 + bullets.length * 40 : detailText ? lineCount(detailText) * 23 + 8 : 0);
      return { bullets, detailText, nameText, nameH, blockH };
    });
    const qh = Math.max(150, ...blocks.map((b) => b.blockH + 30));
    const plotH = (qh + g) * 2;
    const x0 = marginX;
    const y0 = marginY;
    const x1 = x0 + plotW;
    const y1 = y0 + plotH;
    const cx = x0 + plotW / 2;
    const cy = y0 + plotH / 2;
    const edge = ctx.preset.edge;
    const capSize = 18;

    // soft region panels — a 2×2 is a claim about four AREAS, so draw them
    const corners: Array<[number, number]> = [
      [x0, y0],
      [cx + g, y0],
      [x0, cy + g],
      [cx + g, cy + g],
    ];
    items.forEach((item, i) => {
      const role = ctx.role(i, { n: 4, color: item.color });
      const [px, py] = corners[i];
      ctx.item(item.id, () =>
        ctx.shape(
          "rectangle",
          px,
          py,
          qw,
          qh,
          { stroke: role.color, fill: role.softFill, fillStyle: "solid", strokeWidth: 1.4, roughness: ctx.preset.roughness },
          { style: { roundness: role.roundness ?? 10 }, z: -1 },
        ),
      );
    });

    // double-headed arrow axes crossing at the center, drawn over the panels
    ctx.line(
      [
        [x0 - 16, cy],
        [x1 + 16, cy],
      ],
      { color: edge, width: 1.8, z: 1 },
    );
    ctx.arrowhead(cx, cy, x1 + 16, cy, edge, 1.8, 1);
    ctx.arrowhead(cx, cy, x0 - 16, cy, edge, 1.8, 1);
    ctx.line(
      [
        [cx, y0 - 16],
        [cx, y1 + 16],
      ],
      { color: edge, width: 1.8, z: 1 },
    );
    ctx.arrowhead(cx, cy, cx, y0 - 16, edge, 1.8, 1);
    ctx.arrowhead(cx, cy, cx, y1 + 16, edge, 1.8, 1);

    // axis end captions, at the arrow tips and clear of the plot: [negative, positive]
    const xl = optList(spec.options, "xLabels");
    if (xl) {
      ctx.label(xl[0] ?? "", x0 - 26, cy, { size: capSize, color: ctx.mutedInk, align: "right" });
      ctx.label(xl[1] ?? "", x1 + 26, cy, { size: capSize, color: ctx.mutedInk, align: "left" });
    }
    const yl = optList(spec.options, "yLabels");
    if (yl) {
      ctx.label(yl[1] ?? "", cx, y0 - 26, { size: capSize, color: ctx.mutedInk, vAnchor: "middle" });
      ctx.label(yl[0] ?? "", cx, y1 + 26, { size: capSize, color: ctx.mutedInk, vAnchor: "middle" });
    }

    // one content block centred in each region panel
    items.forEach((item, i) =>
      ctx.item(item.id, () => {
        const role = ctx.role(i, { n: 4, color: item.color });
        const [px, py] = corners[i];
        const qcx = px + qw / 2;
        const qcy = py + qh / 2;
        const { bullets, detailText, nameText, nameH, blockH } = blocks[i];
        let y = qcy - blockH / 2;
        if (item.icon) {
          ctx.icon(item.icon, qcx, y + 23, 44, role.color);
          y += 58;
        }
        ctx.label(nameText, qcx, y, {
          size: 24,
          color: role.color,
          font: "heading",
          weight: ctx.preset.fonts.headingWeight,
          vAnchor: "top",
          id: ctx.uid(item.id),
        });
        y += nameH + 4;
        if (bullets.length) {
          bulletList(ctx, bullets, qcx - maxW / 2, y, { dotD: 8, dotColor: role.color, textColor: ctx.ink, maxW: maxW - 24, pitch: 40, size: 17 });
        } else if (detailText) {
          ctx.label(detailText, qcx, y, { size: 18, color: ctx.mutedInk, vAnchor: "top" });
        }
      }),
    );
  },
});

// ---- table ------------------------------------------------------------------

registerViz({
  name: "table",
  category: "Comparison",
  summary: "Grid of individually drawn cells; each column stroked its own color.",
  sweetSpot: { min: 1, max: 8 },
  entryKinds: ["row", "item", "header"],
  generate(spec: VizSpec, ctx: VizContext) {
    const rows = spec.items.filter((i) => i.kind === "row" || i.kind === "item" || i.kind === "header");
    let header: VizItem | undefined;
    const body: VizItem[] = [];
    for (const r of rows) {
      if (!header && (r.kind === "header" || r.opts.header === true)) header = r;
      else body.push(r);
    }
    const cellsOf = (r: VizItem): string[] => [r.label, ...r.strings];
    const all = header ? [header, ...body] : body;
    const cols = Math.max(...all.map((r) => cellsOf(r).length), 1);

    // fit column widths to content
    const colW: number[] = [];
    for (let j = 0; j < cols; j++) {
      let w = 90;
      for (const r of all) {
        const text = cellsOf(r)[j] ?? "";
        w = Math.max(w, ctx.measure(text, r === header ? 20 : 15) + 32);
      }
      colW.push(Math.min(w, 230));
    }
    const colX: number[] = [0];
    for (let j = 0; j < cols; j++) colX.push(colX[j] + colW[j] + 5);

    const headerH = 64;
    const bodyH = 52;
    // Column 0 is ink; every other column's cells are stroked in its own palette color.
    const colColor = (j: number): string => (j === 0 ? ctx.ink : ctx.role(j - 1, { n: Math.max(cols - 1, 1) }).color);
    const cellStyle = (j: number) => ({
      stroke: colColor(j),
      fill: null,
      fillStyle: "none" as const,
      strokeWidth: Math.min(2, ctx.preset.strokeWidth),
      roughness: ctx.preset.roughness,
    });

    let y = 0;
    if (header) {
      cellsOf(header).forEach((text, j) => {
        ctx.shape("rectangle", colX[j], y, colW[j], headerH, cellStyle(j), { id: j === 0 ? ctx.uid(header!.id) : undefined, style: { roundness: 6 } });
        ctx.label(ctx.wrap(text, colW[j] - 14, 20, "heading", 2), colX[j] + colW[j] / 2, y + headerH / 2, {
          size: 20,
          color: colColor(j),
          font: "heading",
          weight: ctx.preset.fonts.headingWeight,
        });
      });
      y += headerH + 5;
    }
    body.forEach((row) => {
      ctx.item(row.id, () => {
        for (let j = 0; j < cols; j++) {
          const text = cellsOf(row)[j] ?? "";
          ctx.shape("rectangle", colX[j], y, colW[j], bodyH, cellStyle(j), { id: j === 0 ? ctx.uid(row.id) : undefined, style: { roundness: 6 } });
          if (text) ctx.label(ctx.wrap(text, colW[j] - 14, 15, undefined, 2), colX[j] + colW[j] / 2, y + bodyH / 2, { size: 15, color: ctx.ink });
        }
      });
      y += bodyH + 5;
    });
  },
});

// ---- pros-and-cons ------------------------------------------------------------

registerViz({
  name: "pros-and-cons",
  aliases: ["pros-cons"],
  category: "Comparison",
  summary: "Two panels — pros with a check, cons with a cross — as bullet lists.",
  sweetSpot: { min: 1, max: 6 },
  entryKinds: ["pro", "con", "item"],
  options: [
    { name: "proColor", type: "string", description: "override the pros panel color" },
    { name: "conColor", type: "string", description: "override the cons panel color" },
  ],
  generate(spec: VizSpec, ctx: VizContext) {
    let pros = spec.items.filter((i) => i.kind === "pro");
    let cons = spec.items.filter((i) => i.kind === "con");
    let proTitle = "Pros";
    let conTitle = "Cons";
    // also accept two group items ("pros" / "cons") whose children are the entries
    for (const g of spec.items) {
      if (!g.children.length) continue;
      const key = `${g.kind} ${g.id} ${g.label}`.toLowerCase();
      if (!pros.length && /\bpros?\b/.test(key)) {
        pros = g.children;
        proTitle = g.label || proTitle;
      } else if (!cons.length && /\bcons?\b/.test(key)) {
        cons = g.children;
        conTitle = g.label || conTitle;
      }
    }
    // Role picks: role(2) tends to read green-ish and role(1) contrasting in the
    // wheel palettes (never hardcoded, so every preset keeps a coherent pair);
    // `proColor:` / `conColor:` options override explicitly.
    const proRole = ctx.role(2, { color: optStr(spec.options, "proColor") });
    const conRole = ctx.role(1, { color: optStr(spec.options, "conColor") });

    const panelW = 300;
    const gap = 26;
    const pad = 20;
    const rows = Math.max(pros.length, cons.length, 1);
    const panelH = 96 + rows * 42 + 10;

    const panel = (x: number, name: string, role: RoleStyle, entries: VizItem[], iconName: string, idHint: string): void => {
      ctx.shape("rectangle", x, 0, panelW, panelH, role, { id: ctx.uid(idHint), style: { roundness: role.roundness ?? 8 } });
      const accent = onPanel(role);
      const body = inPanel(ctx, role);
      ctx.label(name, x + pad, 34, { size: 20, color: accent, align: "left", font: "heading", weight: ctx.preset.fonts.headingWeight });
      ctx.icon(iconName, x + panelW - pad - 20, 32, 38, accent);
      ctx.line(
        [
          [x + pad, 58],
          [x + panelW - pad, 58],
        ],
        { color: accent, width: 1.6 },
      );
      bulletList(ctx, entries, x + pad, 82, { dotColor: accent, textColor: body, maxW: panelW - pad * 2 - 24, scopeItems: true });
    };
    panel(0, proTitle, proRole, pros, "check", "pros");
    panel(panelW + gap, conTitle, conRole, cons, "x", "cons");
  },
});

// ---- versus -------------------------------------------------------------------

registerViz({
  name: "versus",
  aliases: ["vs"],
  category: "Comparison",
  summary: "Two contenders compared criterion by criterion around a center gutter.",
  sweetSpot: { min: 1, max: 6 },
  entryKinds: ["left", "right", "item", "contender", "criterion", "row"],
  generate(spec: VizSpec, ctx: VizContext) {
    let left = spec.items.find((i) => i.kind === "left");
    let right = spec.items.find((i) => i.kind === "right");
    for (const g of itemsOf(spec, "item", "contender")) {
      if (!left && g !== right) left = g;
      else if (!right && g !== left) right = g;
    }
    const criteria = spec.items.filter((i) => i.kind === "criterion" || i.kind === "row");

    const lRole = ctx.role(0, { color: left?.color });
    const rRole = ctx.role(1, { color: right?.color });
    // A wide, short table: two contender columns of claim cards around a
    // criterion gutter. (The old layout stacked 150-unit rows, which made the
    // block squarer than the frame and shrank every glyph to ~20px.)
    const W = 1020;
    const gutterW = 240;
    const sideW = (W - gutterW) / 2;
    const gutterCx = W / 2;
    const headSize = 30;
    const headText = (label: string): string => ctx.wrap(label, sideW - 30, headSize, "heading", 2);
    const lText = headText(left?.label ?? "A");
    const rText = headText(right?.label ?? "B");
    const headerH = Math.max(76, Math.max(lineCount(lText), lineCount(rText)) * headSize * 1.25 + 26);
    const gapY = 14;
    const cardPad = 16;
    const claimSize = 24;
    const nameSize = 22;
    const iconSize = 30;
    const claimMaxW = sideW - 56;

    // per-side claims: `left:`/`right:` opts, or the first/second child
    const rows = criteria.map((c) => {
      const lChild = c.children[0];
      const rChild = c.children[1];
      const lClaim = optStr(c.opts, "left") ?? lChild?.label;
      const rClaim = optStr(c.opts, "right") ?? rChild?.label;
      const hasIcon = !!c.icon;
      const nameText = ctx.wrap(c.label, gutterW - 32 - (hasIcon ? iconSize + 10 : 0), nameSize, "heading", 3);
      const nameH = lineCount(nameText) * nameSize * 1.25;
      const lh = lClaim ? ctx.measureLabelBlock(lClaim, lChild?.detail, { maxW: claimMaxW, size: claimSize }).h : 0;
      const rh = rClaim ? ctx.measureLabelBlock(rClaim, rChild?.detail, { maxW: claimMaxW, size: claimSize }).h : 0;
      const h = Math.max(80, lh + cardPad * 2, rh + cardPad * 2, nameH + 16);
      return { c, lChild, rChild, lClaim, rClaim, hasIcon, nameText, h };
    });

    // contender header bars
    inItem(ctx, left, () => {
      ctx.shape("rectangle", 0, 0, sideW, headerH, lRole, { id: ctx.uid(left?.id ?? "left"), style: { roundness: lRole.roundness ?? 8 } });
      ctx.label(lText, sideW / 2, headerH / 2, { size: headSize, color: onPanel(lRole), font: "heading", weight: 700 });
    });
    inItem(ctx, right, () => {
      ctx.shape("rectangle", W - sideW, 0, sideW, headerH, rRole, { id: ctx.uid(right?.id ?? "right"), style: { roundness: rRole.roundness ?? 8 } });
      ctx.label(rText, W - sideW / 2, headerH / 2, { size: headSize, color: onPanel(rRole), font: "heading", weight: 700 });
    });

    // one row per criterion: a tinted claim card under each contender, the
    // icon + criterion name between them — all three centred on one line.
    // The cards are fill-only so the outlined header bar stays the heading.
    const card = (role: RoleStyle, x: number, y: number, h: number): void => {
      ctx.shape(
        "rectangle",
        x,
        y,
        sideW,
        h,
        { stroke: "transparent", fill: role.softFill, fillStyle: "solid", strokeWidth: 0, roughness: ctx.preset.roughness },
        { style: { roundness: role.roundness ?? 8 } },
      );
    };
    let y = headerH + gapY;
    for (const r of rows) {
      const cy = y + r.h / 2;
      const top = y;
      ctx.item(r.c.id, () => {
        const nameW = Math.max(...r.nameText.split("\n").map((l) => ctx.measure(l, nameSize, "heading")));
        const drewIcon = r.hasIcon && ctx.icon(r.c.icon, gutterCx - (iconSize + 10 + nameW) / 2 + iconSize / 2, cy, iconSize, ctx.ink);
        const nameX = drewIcon ? gutterCx - (iconSize + 10 + nameW) / 2 + iconSize + 10 : gutterCx;
        ctx.label(r.nameText, nameX, cy, {
          size: nameSize,
          color: ctx.ink,
          font: "heading",
          weight: ctx.preset.fonts.headingWeight,
          align: drewIcon ? "left" : "center",
        });
        if (r.lClaim) {
          card(lRole, 0, top, r.h);
          ctx.labelBlock(r.lClaim, r.lChild?.detail, sideW / 2, cy, { color: lRole.color, align: "center", maxW: claimMaxW, size: claimSize });
        }
        if (r.rClaim) {
          card(rRole, W - sideW, top, r.h);
          ctx.labelBlock(r.rClaim, r.rChild?.detail, W - sideW / 2, cy, { color: rRole.color, align: "center", maxW: claimMaxW, size: claimSize });
        }
      });
      y += r.h + gapY;
    }
  },
});

// ---- problem-solution -----------------------------------------------------------

registerViz({
  name: "problem-solution",
  category: "Problems and Solutions",
  summary: "Problem → central solution circle → outcome, with support captions.",
  sweetSpot: { min: 3, max: 6 },
  entryKinds: ["item", "problem", "solution", "outcome", "support"],
  generate(spec: VizSpec, ctx: VizContext) {
    const generic = itemsOf(spec, "item");
    const problem = spec.items.find((i) => i.kind === "problem") ?? generic[0];
    const solution = spec.items.find((i) => i.kind === "solution") ?? generic[1];
    const outcome = spec.items.find((i) => i.kind === "outcome") ?? generic[2];
    const supports = spec.items.filter((i) => i.kind === "support");

    const pRole = ctx.role(1, { color: problem?.color });
    const oRole = ctx.role(2, { color: outcome?.color });
    const cx = 400;
    const ccy = 140;
    const D = 180;

    // central solution circle (ink outline)
    inItem(ctx, solution, () => {
      ctx.shape(
        "circle",
        cx - D / 2,
        ccy - D / 2,
        D,
        D,
        { stroke: ctx.ink, fill: null, fillStyle: "none", strokeWidth: ctx.preset.strokeWidth, roughness: ctx.preset.roughness },
        { id: ctx.uid(solution?.id ?? "solution") },
      );
      if (solution) {
        const text = ctx.wrap(solution.label, D - 44, 18, "heading", 3);
        const detail = solution.detail ? ctx.wrap(solution.detail, D - 44, 13, undefined, 3) : undefined;
        const th = lineCount(text) * 23;
        const dh = detail ? lineCount(detail) * 16 + 8 : 0;
        ctx.label(text, cx, ccy - dh / 2, { size: 18, color: ctx.ink, font: "heading", weight: ctx.preset.fonts.headingWeight });
        if (detail) ctx.label(detail, cx, ccy + th / 2 + 8, { size: 13, color: ctx.mutedInk });
      }
    });

    // side tile + label block (problem left, outcome right); the tiles sit on
    // the circle's own centre line so the flow reads as one straight row
    const tileS = 84;
    const tileTop = ccy - tileS / 2;
    const tile = (item: VizItem | undefined, role: RoleStyle, tx: number, fallbackIcon: string, hint: string): void => {
      if (!item) return;
      ctx.item(item.id, () => {
        ctx.shape("rectangle", tx, tileTop, tileS, tileS, role, { id: ctx.uid(item.id ?? hint), style: { roundness: role.roundness ?? 10 } });
        ctx.icon(item.icon ?? fallbackIcon, tx + tileS / 2, ccy, 44, onPanel(role));
        ctx.labelBlock(item.label, item.detail, tx + tileS / 2, tileTop + tileS + 14, { color: role.color, align: "center", maxW: 220, vAnchor: "top" });
      });
    };
    const pX = 60;
    const oX = 656;
    tile(problem, pRole, pX, "trend-down", "problem");
    tile(outcome, oRole, oX, "trend-up", "outcome");

    // flow arrows into and out of the solution: horizontal, tile edge to circle
    // edge, with the same air at both ends
    if (problem) ctx.arrow(pX + tileS + 12, ccy, cx - D / 2 - 12, ccy, { color: ctx.preset.edge, width: 1.6 });
    if (outcome) ctx.arrow(cx + D / 2 + 12, ccy, oX - 12, ccy, { color: ctx.preset.edge, width: 1.6 });

    // T-connector down to support captions
    if (supports.length) {
      const topY = ccy + D / 2 + 6;
      const busY = topY + 44;
      const spread = Math.min(220, 560 / supports.length);
      const first = cx - ((supports.length - 1) * spread) / 2;
      ctx.line(
        [
          [cx, topY],
          [cx, busY],
        ],
        { color: ctx.preset.edge, width: 1.4 },
      );
      if (supports.length > 1) {
        ctx.line(
          [
            [first, busY],
            [first + (supports.length - 1) * spread, busY],
          ],
          { color: ctx.preset.edge, width: 1.4 },
        );
      }
      supports.forEach((s, i) =>
        ctx.item(s.id, () => {
          const sx = first + i * spread;
          ctx.line(
            [
              [sx, busY],
              [sx, busY + 16],
            ],
            { color: ctx.preset.edge, width: 1.4 },
          );
          ctx.label(ctx.wrap(s.label, spread - 24, 15), sx, busY + 30, { size: 15, color: ctx.mutedInk, vAnchor: "top" });
        }),
      );
    }
  },
});

// ---- transformation ---------------------------------------------------------------

registerViz({
  name: "transformation",
  aliases: ["before-after"],
  category: "Problems and Solutions",
  summary: "A suspension bridge carrying you from before to after (reference design).",
  entryKinds: ["item", "before", "after"],
  sweetSpot: { min: 2, max: 2 },
  generate(spec: VizSpec, ctx: VizContext) {
    const generic = itemsOf(spec, "item");
    const before = spec.items.find((i) => i.kind === "before") ?? generic[0];
    const after = spec.items.find((i) => i.kind === "after") ?? generic[1];
    const bRole = ctx.role(1, { color: before?.color });
    const aRole = ctx.role(2, { color: after?.color });

    const x0 = 150;
    // wide span: the card is height-bound, so the width is free and buys
    // room for bigger box type
    const bandW = 800;
    const edge = ctx.preset.edge;
    const cx = x0 + bandW / 2;

    // suspension bridge: deck, two towers, cables with vertical hangers
    const deckY = 190;
    const topY = 30;
    const t1 = x0 + bandW * 0.28;
    const t2 = x0 + bandW * 0.72;
    // cable height above the deck at position x (piecewise curves; the sag
    // between towers dips to ~55px, the side spans sweep down to the ends)
    const cableY = (x: number): number => {
      if (x <= t1) {
        const u = (x - x0) / (t1 - x0);
        return deckY - (deckY - topY) * u * u;
      }
      if (x >= t2) {
        const u = (x0 + bandW - x) / (x0 + bandW - t2);
        return deckY - (deckY - topY) * u * u;
      }
      const u = (x - t1) / (t2 - t1);
      const sag = deckY - 55;
      return topY + (sag - topY) * (1 - (2 * u - 1) * (2 * u - 1));
    };
    // deck
    ctx.line(
      [
        [x0, deckY],
        [x0 + bandW, deckY],
      ],
      { color: edge, width: 2.4 },
    );
    // towers (above deck + a short leg below with a footing); the legs stop
    // well clear of the description boxes, so nothing reads as standing on them
    const footY = deckY + 34;
    for (const tx of [t1, t2]) {
      ctx.line(
        [
          [tx, topY - 6],
          [tx, footY],
        ],
        { color: edge, width: 3 },
      );
      ctx.line(
        [
          [tx - 12, footY],
          [tx + 12, footY],
        ],
        { color: edge, width: 2 },
      );
    }
    // cables: sampled piecewise curve across the full span
    const cablePts: Array<[number, number]> = [];
    const SEG = 64;
    for (let s = 0; s <= SEG; s++) {
      const x = x0 + (bandW * s) / SEG;
      cablePts.push([x, cableY(x)]);
    }
    ctx.line(cablePts, { color: edge, width: 2 });
    // hangers: verticals from cable to deck
    const N = 30;
    for (let i = 1; i < N; i++) {
      const hx = x0 + (bandW * i) / N;
      const hy = cableY(hx);
      if (deckY - hy < 8) continue;
      ctx.line(
        [
          [hx, hy],
          [hx, deckY],
        ],
        { color: edge, width: 1.3 },
      );
    }

    // The state names live in the description boxes below — printing them a
    // second time at the bridge ends put every word on the card twice.

    // two description boxes below, arrow between
    const boxW = 262;
    const boxH = 100;
    const boxY = footY + 30;
    const box = (item: VizItem | undefined, role: RoleStyle, bx: number, hint: string): void => {
      if (!item) return;
      ctx.item(item.id, () => {
        ctx.shape("rectangle", bx, boxY, boxW, boxH, role, { id: ctx.uid(item.id ?? hint), style: { roundness: role.roundness ?? 8 } });
        const accent = onPanel(role);
        if (item.detail) {
          ctx.label(item.label, bx + boxW / 2, boxY + 28, { size: 20, color: accent, weight: 700, font: "heading", maxW: boxW - 28, maxLines: 1 });
          ctx.label(ctx.wrap(item.detail, boxW - 28, 15, undefined, 2), bx + boxW / 2, boxY + 64, { size: 15, color: inPanel(ctx, role) });
        } else {
          ctx.label(item.label, bx + boxW / 2, boxY + boxH / 2, { size: 20, color: accent, weight: 700, font: "heading", maxW: boxW - 28, maxLines: 2 });
        }
      });
    };
    box(before, bRole, t1 - boxW / 2, "before");
    box(after, aRole, t2 - boxW / 2, "after");
    // arrow between the boxes with 12 units of air at both ends
    ctx.arrow(t1 + boxW / 2 + 12, boxY + boxH / 2, t2 - boxW / 2 - 12, boxY + boxH / 2, { color: edge, width: 2 });
  },
});
